/**
 * ConnectSnippets -- "how to connect" per client (FEAT-033, TASK-010, design.md C.4).
 *
 * Content comes EXCLUSIVELY from buildConnectSnippets (utils/gatewaySnippets.ts): no
 * component writes the environment variable names or gateway paths by hand. The gateway bases
 * are the `base_urls` returned by the API (D32 addendum): the browser never composes them and
 * never falls back to the page origin -- without base_urls an honest "unavailable" alert is
 * shown instead of snippets. With
 * secret === null the snippets carry the masked prefix (maskSecret) and a note explains
 * that the full secret only appears on create/rotate -- no fake example secret is ever shown.
 *
 * mode="compact" renders only the Claude Code block, without tabs (Code -> Local connection).
 * Shared by the API key tab, the reveal modal, "Connect my Claude Code" and the Code card.
 */

import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Copy, CursorClick, GlobeSimple, Terminal, TerminalWindow } from '@phosphor-icons/react';
import { Alert, TabPanel, Tabs, useToast } from '@/components/ui';
import type { TabItem } from '@/components/ui';
import { buildConnectSnippets, type GatewayBaseUrls } from '@/utils/gatewaySnippets';
// --- FEAT-056 TASK-007 (create or rotate link) ---
import { CreateOrRotateLink } from './CreateOrRotateLink';
// --- /FEAT-056 TASK-007 (create or rotate link) ---

export type SnippetClient = 'claude_code' | 'codex' | 'cursor' | 'curl';

export interface ConnectSnippetsProps {
  /** key_prefix of the key (feeds maskSecret in masked mode). */
  keyPrefix: string;
  /** Full secret right after create/rotate; null = masked mode. */
  secret: string | null;
  /** base_urls from the API (verbatim). null/undefined = not available -> alert, no snippet. */
  baseUrls: GatewayBaseUrls | null | undefined;
  /** 'compact' = Claude Code only, no tabs. */
  mode?: 'full' | 'compact';
  defaultClient?: SnippetClient;
  /** Unique per instance (Tabs / TabPanel ids). */
  idPrefix: string;
  // --- FEAT-056 TASK-007 (create or rotate link) ---
  /** Shows the "Create or rotate key" link (masked mode) and runs this on click. */
  onCreateOrRotate?: () => void;
  // --- /FEAT-056 TASK-007 (create or rotate link) ---
}

// ---------------------------------------------------------------------------
// Clipboard helper (shared with RevealSecretModal)
// ---------------------------------------------------------------------------

/**
 * Copies text and toasts the result. Never logs the text (it may be a secret).
 * Returns true on success.
 */
export function useCopyToClipboard() {
  const intl = useIntl();
  const { addToast } = useToast();

  return useCallback(
    async (text: string, successTitle: string): Promise<boolean> => {
      try {
        if (!navigator.clipboard?.writeText) throw new Error('clipboard unavailable');
        await navigator.clipboard.writeText(text);
        addToast({ variant: 'success', title: successTitle });
        return true;
      } catch {
        addToast({ variant: 'error', title: intl.formatMessage({ id: 'settings.apiKey.reveal.copyError' }) });
        return false;
      }
    },
    [addToast, intl],
  );
}

// ---------------------------------------------------------------------------
// SnippetBlock (local)
// ---------------------------------------------------------------------------

function SnippetBlock({ label, code }: { label: string; code: string }) {
  const intl = useIntl();
  const copy = useCopyToClipboard();

  return (
    <div className="space-y-1">
      <p className="text-xs text-text-tertiary">{label}</p>
      <div className="relative">
        <pre className="font-mono text-xs text-text-primary whitespace-pre-wrap break-all rounded-md bg-bg-tertiary p-3 pr-12">
          {code}
        </pre>
        <button
          type="button"
          onClick={() => void copy(code, intl.formatMessage({ id: 'settings.apiKey.snippets.copied' }))}
          aria-label={intl.formatMessage({ id: 'settings.apiKey.snippets.copy' })}
          className="absolute top-2 right-2 inline-flex items-center justify-center rounded-md p-1.5 text-text-tertiary hover:bg-bg-hover hover:text-text-primary transition-colors cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus"
        >
          <Copy size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ConnectSnippets({
  keyPrefix,
  secret,
  baseUrls,
  mode = 'full',
  defaultClient = 'claude_code',
  idPrefix,
  onCreateOrRotate,
}: ConnectSnippetsProps) {
  const intl = useIntl();
  const [client, setClient] = useState<string>(defaultClient);

  const snippets = useMemo(
    () => (baseUrls ? buildConnectSnippets({ baseUrls, secret, keyPrefix }) : null),
    [baseUrls, secret, keyPrefix],
  );

  if (!snippets) {
    return (
      <Alert variant="warning">
        {intl.formatMessage({ id: 'settings.apiKey.snippets.baseUrlsUnavailable' })}
      </Alert>
    );
  }

  const maskedNote =
    secret === null ? (
      <div className="space-y-1">
        <p className="text-xs text-text-tertiary">
          {intl.formatMessage({ id: 'settings.apiKey.snippets.maskedNote' })}
        </p>
        {/* --- FEAT-056 TASK-007 (create or rotate link) --- */}
        {onCreateOrRotate ? <CreateOrRotateLink onClick={onCreateOrRotate} /> : null}
        {/* --- /FEAT-056 TASK-007 (create or rotate link) --- */}
      </div>
    ) : null;

  const claudeCodeBlocks = (
    <div className="space-y-3">
      <SnippetBlock
        label={intl.formatMessage({ id: 'settings.apiKey.snippets.claudeCode.shellLabel' })}
        code={snippets.claudeCodeEnv}
      />
      <SnippetBlock
        label={intl.formatMessage({ id: 'settings.apiKey.snippets.claudeCode.settingsLabel' })}
        code={snippets.claudeCodeSettingsJson}
      />
    </div>
  );

  if (mode === 'compact') {
    return (
      <div className="space-y-3">
        {claudeCodeBlocks}
        {maskedNote}
      </div>
    );
  }

  const tabItems: TabItem[] = [
    {
      value: 'claude_code',
      label: intl.formatMessage({ id: 'settings.apiKey.snippets.tab.claudeCode' }),
      icon: <Terminal size={16} aria-hidden="true" />,
    },
    {
      value: 'codex',
      label: intl.formatMessage({ id: 'settings.apiKey.snippets.tab.codex' }),
      icon: <TerminalWindow size={16} aria-hidden="true" />,
    },
    {
      value: 'cursor',
      label: intl.formatMessage({ id: 'settings.apiKey.snippets.tab.cursor' }),
      icon: <CursorClick size={16} aria-hidden="true" />,
    },
    {
      value: 'curl',
      label: intl.formatMessage({ id: 'settings.apiKey.snippets.tab.curl' }),
      icon: <GlobeSimple size={16} aria-hidden="true" />,
    },
  ];

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-text-primary">
        {intl.formatMessage({ id: 'settings.apiKey.snippets.title' })}
      </h3>
      <Tabs
        items={tabItems}
        value={client}
        onChange={setClient}
        ariaLabel={intl.formatMessage({ id: 'settings.apiKey.snippets.ariaLabel' })}
        idPrefix={idPrefix}
      />

      <TabPanel value="claude_code" activeValue={client} idPrefix={idPrefix}>
        {claudeCodeBlocks}
      </TabPanel>

      <TabPanel value="codex" activeValue={client} idPrefix={idPrefix}>
        <SnippetBlock
          label={intl.formatMessage({ id: 'settings.apiKey.snippets.codex.shellLabel' })}
          code={snippets.codexEnv}
        />
      </TabPanel>

      <TabPanel value="cursor" activeValue={client} idPrefix={idPrefix}>
        <Alert variant="warning">{intl.formatMessage({ id: snippets.cursorNotice.messageId })}</Alert>
      </TabPanel>

      <TabPanel value="curl" activeValue={client} idPrefix={idPrefix}>
        <div className="space-y-2">
          <SnippetBlock
            label={intl.formatMessage({ id: 'settings.apiKey.snippets.curl.label' })}
            code={snippets.curl}
          />
          <p className="text-xs text-text-tertiary">
            {intl.formatMessage({ id: 'settings.apiKey.snippets.curl.availabilityNote' })}
          </p>
        </div>
      </TabPanel>

      {maskedNote}

    </div>
  );
}

export default ConnectSnippets;
